/**
 * The onboarding state machine.
 *
 * Where a vendor stands in setup is **derived, never stored**: every state
 * below is computed from two queries the API already returns
 * (`integrations.meta.status` and `integrations.meta.onboardingMethods`), plus
 * — only when a manual connection exists — `getWhatsAppManualConfig`. No new
 * column, no migration, and no possibility of the stored progress disagreeing
 * with reality.
 *
 * The finish line is `hasReceivedFirstMessage`, not "a token was saved". A
 * connection that has never carried a real inbound message is not proven, and
 * saying otherwise is the single most misleading thing onboarding could do.
 *
 * Kept as a pure function, separate from the hook that feeds it, so the
 * precedence rules can be tested without mounting React or mocking a query
 * client.
 */

export type SetupState =
  | 'BLOCKED_BY_PLATFORM'
  | 'NOT_CONNECTED'
  | 'CONNECTING'
  | 'CANNOT_RECEIVE'
  | 'UNPROVEN'
  | 'LIVE';

/** Corridor step each state resolves to. */
export type SetupStep = 1 | 2 | 3 | 4 | 5;

export interface SetupStatusInput {
  whatsapp: Array<{
    displayPhoneNumber: string | null;
    verifiedName: string | null;
    source: string | null;
    status: string | null;
    webhookSubscribed: boolean;
  }>;
  facebook: Array<unknown>;
  instagram: Array<unknown>;
  hasReceivedFirstMessage: boolean;
  isWebhookVerified: boolean;
}

export interface SetupMethodsInput {
  configReason: 'ok' | 'no_app' | 'no_config_id';
}

export interface SetupManualInput {
  /** Whether the vendor saved the App Secret for their own Meta app. */
  hasBYOCAppSecret: boolean;
}

export interface SetupPersistedInput {
  skippedSteps: number[];
  bannerDismissedAt: string | null;
  completedAt: string | null;
}

export interface SetupSnapshot {
  state: SetupState;
  step: SetupStep;
  /** Rendered for SuperAdmins only — a vendor can do nothing with it. */
  blockedReason: 'no_app' | 'no_config_id' | null;
  primaryNumber: { displayPhoneNumber: string | null; verifiedName: string | null } | null;
  /** True when CANNOT_RECEIVE is specifically the missing-BYOC-secret case. */
  needsAppSecret: boolean;
  /** True when the remedy is simply to (re)subscribe the webhook. */
  needsWebhookSubscribe: boolean;
  hasOtherChannels: boolean;
  skippedSteps: number[];
  completedAt: string | null;
  /** Whether the dashboard should nudge them back into setup. */
  showBanner: boolean;
}

const STEP_FOR_STATE: Record<SetupState, SetupStep> = {
  BLOCKED_BY_PLATFORM: 1,
  NOT_CONNECTED: 1,
  CONNECTING: 1,
  CANNOT_RECEIVE: 2,
  UNPROVEN: 2,
  LIVE: 5,
};

export function resolveSetupState(input: {
  status: SetupStatusInput;
  methods: SetupMethodsInput;
  /** Only fetched when a manual connection exists; absent otherwise. */
  manual?: SetupManualInput | null;
  persisted?: SetupPersistedInput | null;
}): SetupSnapshot {
  const { status, methods, manual, persisted } = input;

  const primary = status.whatsapp[0] ?? null;
  const isManual = primary?.source === 'manual';

  // A manual connection whose BYOC app secret is missing cannot verify the
  // signature on any inbound webhook, so every message Meta sends is dropped.
  // The vendor sees a working connection and silence.
  const needsAppSecret = isManual && manual != null && !manual.hasBYOCAppSecret;

  // Narrowed here rather than off `state` below: TypeScript can't infer
  // `configReason !== 'ok'` from a comparison against the state enum.
  const blockedReason = methods.configReason === 'ok' ? null : methods.configReason;

  let state: SetupState;
  if (blockedReason) {
    // The platform's own Meta setup is incomplete. Nothing the vendor does can
    // move this, so it outranks every other state — including "not connected",
    // which would otherwise send them at a button that cannot work.
    state = 'BLOCKED_BY_PLATFORM';
  } else if (status.whatsapp.length === 0) {
    state = 'NOT_CONNECTED';
  } else if (primary?.status !== 'active') {
    state = 'CONNECTING';
  } else if (!status.isWebhookVerified || needsAppSecret) {
    // Sending works, receiving doesn't. Ranked above UNPROVEN because the test
    // message in step 2 can never complete while inbound is broken.
    state = 'CANNOT_RECEIVE';
  } else if (!status.hasReceivedFirstMessage) {
    state = 'UNPROVEN';
  } else {
    state = 'LIVE';
  }

  const completedAt = persisted?.completedAt ?? null;

  return {
    state,
    step: STEP_FOR_STATE[state],
    blockedReason,
    primaryNumber: primary
      ? { displayPhoneNumber: primary.displayPhoneNumber, verifiedName: primary.verifiedName }
      : null,
    needsAppSecret,
    needsWebhookSubscribe: state === 'CANNOT_RECEIVE' && !needsAppSecret,
    hasOtherChannels: status.facebook.length > 0 || status.instagram.length > 0,
    skippedSteps: persisted?.skippedSteps ?? [],
    completedAt,
    // Never nag someone who has finished, someone who asked us to stop, or
    // someone whose blocker is on our side of the fence.
    showBanner:
      state !== 'LIVE' &&
      state !== 'BLOCKED_BY_PLATFORM' &&
      completedAt === null &&
      !persisted?.bannerDismissedAt,
  };
}

/** How many corridor steps still stand between here and a proven connection. */
export function stepsRemaining(state: SetupState): number {
  switch (state) {
    case 'LIVE':
      return 0;
    case 'UNPROVEN':
    case 'CANNOT_RECEIVE':
      return 1;
    default:
      return 2;
  }
}
